import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { categoryConfigs } from "@/mocks/categories";
import { specials } from "@/mocks/specials";
import {
  getFeaturedMovies,
  getHotMovies,
  getLatestNews,
  getLatestMovies,
} from "@/utils/movieData";
import HeroCarousel from "./components/HeroCarousel";
import EditorialPicks from "./components/EditorialPicks";
import MovieCard from "@/components/feature/MovieCard";
import SectionTitle from "@/components/feature/SectionTitle";

export default function Home() {
  useDocumentMeta({
    title: "秋霞电影 - 最新电影资讯与精彩电影推荐",
    description:
      "秋霞电影提供丰富的电影资讯、影片推荐和行业动态覆盖热门电影、经典影片及影视新闻，助您轻松查找各类好电影。",
    keywords: "秋霞电影,电影资讯,影视推荐,热门电影,经典影片,电影动态,最新电影",
  });

  const featured = getFeaturedMovies();
  const hot = getHotMovies(8);
  const latestNews = getLatestNews(6);
  const latestMovies = getLatestMovies(4);

  return (
    <div className="bg-background-50">
      <HeroCarousel movies={featured} />

      {/* quick categories */}
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <h1 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
            秋霞电影 · 看片不再纠结
          </h1>
          <p className="mt-1.5 text-sm text-foreground-600">
            精选热门、经典与最新影片，按类型快速发现你想看的那一部。
          </p>
          <div className="mt-5 flex flex-wrap gap-2.5">
            {categoryConfigs.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:bg-primary-100 hover:text-primary-800"
              >
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* editorial picks */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <SectionTitle
            eyebrow="编辑精选"
            icon={<i className="ri-award-line"></i>}
            title="本周值得一看的影片"
            subtitle="由秋霞电影编辑部挑选，口碑与观赏性兼具"
            moreTo="/category/classic"
            moreLabel="更多精选"
          />
          <EditorialPicks movies={featured.slice(0, 4)} />
        </div>
      </section>

      {/* hot movies */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <SectionTitle
            eyebrow="热门推荐"
            icon={<i className="ri-fire-line"></i>}
            title="当下最受关注的电影"
            subtitle="按热度排序，看看大家都在看什么"
            moreTo="/category/popular"
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {hot.map((movie, index) => (
              <MovieCard key={movie.id} movie={movie} rank={index + 1} />
            ))}
          </div>
        </div>
      </section>

      {/* news + specials */}
      <section className="bg-background-50">
        <div className="mx-auto grid max-w-[1280px] grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionTitle
              eyebrow="影视资讯"
              icon={<i className="ri-newspaper-line"></i>}
              title="最新电影资讯快讯"
              subtitle="行业动态、影片公告与活动信息"
              moreTo="/news"
            />
            <ul className="divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
              {latestNews.map((n) => (
                <li key={n.id}>
                  <Link to={`/news/detail/${n.id}`} className="group flex items-start gap-4 p-4 transition-colors hover:bg-background-100">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-article-line"></i>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground-900 group-hover:text-primary-700 transition-colors md:text-base">
                        {n.title}
                      </span>
                      <span className="mt-1 line-clamp-1 block text-xs text-foreground-600">{n.summary}</span>
                    </span>
                    <span className="shrink-0 text-xs text-foreground-500 whitespace-nowrap">{n.publishTime}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/news"
              className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-lg border border-background-300 bg-background-50 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 sm:hidden"
            >
              查看更多资讯
            </Link>
          </div>

          <div>
            <SectionTitle
              eyebrow="主题专题"
              icon={<i className="ri-bookmark-3-line"></i>}
              title="热门专题策划"
              subtitle="按主题成集，一次看个够"
            />
            <div className="space-y-4">
              {specials.slice(0, 3).map((s) => (
                <Link
                  key={s.id}
                  to={`/special/${s.id}`}
                  className="group block overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-accent-400"
                >
                  <div className="relative aspect-[2/1] w-full overflow-hidden bg-background-200">
                    <img
                      src={s.cover}
                      alt={`${s.title} 专题封面`}
                      title={`${s.title} - 秋霞电影专题`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 to-transparent"></div>
                    <h3 className="absolute bottom-3 left-3 right-3 font-heading text-base font-bold text-background-50">
                      {s.title}
                    </h3>
                  </div>
                  <p className="line-clamp-2 p-3 text-xs leading-relaxed text-foreground-600">{s.intro}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* latest releases */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <SectionTitle
            eyebrow="最新上映"
            icon={<i className="ri-calendar-line"></i>}
            title="新片抢先看"
            subtitle="近期上映的最新影片"
            moreTo="/category/latest"
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {latestMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}